import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import translations from '../i18n/translations.json';

const LanguageContext = createContext(null);

// Supported languages
export const SUPPORTED_LANGUAGES = {
  'pt-BR': {
    code: 'pt-BR',
    name: 'Português',
    flag: '🇧🇷'
  },
  'en-US': {
    code: 'en-US',
    name: 'English',
    flag: '🇺🇸'
  }
};

// Helper to detect system language
const getSystemLanguage = () => {
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language || navigator.languages?.[0] || 'pt-BR';
    
    // Check if it's Portuguese (any variant)
    if (browserLang.startsWith('pt')) {
      return 'pt-BR';
    }
    // Check if it's English (any variant)
    if (browserLang.startsWith('en')) {
      return 'en-US';
    }
  }
  // Default to Portuguese
  return 'pt-BR';
};

// Helper to get nested value from object using dot notation
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, user, updateUserPreferences }) => {
  // languagePreference: 'system' | 'pt-BR' | 'en-US'
  const [languagePreference, setLanguagePreference] = useState(() => {
    // If user is logged in, use their preference
    if (user?.language_preference) {
      return user.language_preference;
    }
    // Otherwise, use localStorage (for non-logged users)
    const saved = localStorage.getItem('viajejunto-language-preference');
    return saved || 'system';
  });

  // Track system language for reactivity
  const [systemLanguage, setSystemLanguage] = useState(getSystemLanguage);

  // Sync with user preferences when user changes (login/logout)
  useEffect(() => {
    if (user?.language_preference) {
      setLanguagePreference(user.language_preference);
    }
  }, [user?.language_preference]);

  // Derive resolved language from preference and system language
  const resolvedLanguage = useMemo(() => {
    return languagePreference === 'system' ? systemLanguage : languagePreference;
  }, [languagePreference, systemLanguage]);

  // Listen for browser language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setSystemLanguage(getSystemLanguage());
    };

    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);

  // Persist preference and update document language
  useEffect(() => {
    localStorage.setItem('viajejunto-language-preference', languagePreference);
    document.documentElement.lang = resolvedLanguage;
  }, [languagePreference, resolvedLanguage]);

  const setLanguage = useCallback((newLang) => {
    if (newLang === 'system' || SUPPORTED_LANGUAGES[newLang]) {
      setLanguagePreference(newLang);
      
      // If user is logged in, save to backend
      if (updateUserPreferences) {
        updateUserPreferences({ language_preference: newLang }).catch(err => {
          console.error('Error saving language preference:', err);
        });
      }
    }
  }, [updateUserPreferences]);

  // Translation function - supports both:
  // 1. String key path: t('dashboard.greeting') -> looks up in translations.json
  // 2. Object with translations: t({ 'pt-BR': 'Olá', 'en-US': 'Hello' }) -> legacy support
  const t = useCallback((keyOrTranslations, params = {}) => {
    // If it's already a plain string (no key lookup needed), return it
    if (typeof keyOrTranslations === 'string' && !keyOrTranslations.includes('.')) {
      // Check if it's a simple key in common
      const simpleValue = getNestedValue(translations, `common.${keyOrTranslations}`);
      if (simpleValue) {
        return simpleValue[resolvedLanguage] || simpleValue['pt-BR'] || keyOrTranslations;
      }
      return keyOrTranslations;
    }
    
    // If it's a dot-notation string key, look up in translations
    if (typeof keyOrTranslations === 'string') {
      const value = getNestedValue(translations, keyOrTranslations);
      if (!value) {
        console.warn(`Translation not found: ${keyOrTranslations}`);
        return keyOrTranslations;
      }
      
      // Get the translated string
      let result = value[resolvedLanguage] || value['pt-BR'] || keyOrTranslations;
      
      // Replace any {{param}} placeholders with actual values
      Object.entries(params).forEach(([key, val]) => {
        result = result.replace(new RegExp(`{{${key}}}`, 'g'), val);
      });
      
      return result;
    }
    
    // Legacy support: if it's an object with translations
    if (typeof keyOrTranslations === 'object' && keyOrTranslations !== null) {
      return keyOrTranslations[resolvedLanguage] || keyOrTranslations['pt-BR'] || Object.values(keyOrTranslations)[0];
    }
    
    return keyOrTranslations;
  }, [resolvedLanguage]);

  return (
    <LanguageContext.Provider value={{
      language: resolvedLanguage,
      languagePreference,
      setLanguage,
      isSystem: languagePreference === 'system',
      supportedLanguages: SUPPORTED_LANGUAGES,
      t,
      translations // Export translations for components that need direct access
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
